import React from 'react';

import { get, isEmpty } from 'lodash';
import { Navigate, RouterProvider, ScrollRestoration, createBrowserRouter } from 'react-router-dom';
import { useErrorBoundary } from 'react-error-boundary';
import { ApolloErrorThrower, LoadingSpinner, useSetAnalyticUserProperties } from '../components';
import { AppContext } from '../contexts/app_context';
import tokenManager from '../utils/token_manager';

import { AuthLayout } from '../pages/auth/layout';

import RootError from '../pages/error';
import RootLayout from '../pages/layout';

import { IsFindTempUserGuard } from './is_find_temp_user_guard';
import { IsForceChangePasswordGuard } from './is_force_change_password_guard';
import { IsHaveRestaurantGuard } from './is_have_restaurant_guard';
import { IsInfoCompleteGuard } from './is_info_complete_guard';
import { IsLoginGuard } from './is_login_guard';
import { IsPdpaGuard } from './is_pdpa_guard';
import { LoggedInRedirectGuard } from './logged_in_redirect_guard';
import { useLoadInfoCompleteStatusContext } from './use_load_info_complete_status_context';
import { useLoadRestaurantsContext } from './use_load_restaurants_context';
import { useLoadUserInfoContext } from './use_load_user_info_context';

import { RegisterPage } from '../pages/auth/register';
import { ResetPasswordPage } from '../pages/auth/reset_password';
import { SignInPage } from '../pages/auth/sign_in';
import { ThankYouPage } from '../pages/auth/thank_you';

import { ForceChangePasswordPage } from '../pages/force_change_password';
import { PdpaAgreementPage } from '../pages/pdpa_agreement';

import CheckOutCreditCardPage from '../pages/credit/check_out_credit_card';
import CheckOutCreditCardPendingPage from '../pages/credit/check_out_credit_card_pending';
import CheckOutQrPage from '../pages/credit/check_out_qr';
import CreditPage from '../pages/credit/page/page';
import { CreditPurchasePage } from '../pages/credit/purchase/page';

import { JobsLayout } from '../pages/jobs/page/layout';
import JobDetailLayout from '../pages/jobs/[id]/layout';
import PeriodJobDetailLayout from '../pages/jobs/period_jobs/[id]/layout';
import JobCreateLayout from '../pages/jobs/new/layout';
import JobCreatePage from '../pages/jobs/new/page';
import JobCreatePeriodPage from '../pages/jobs/period_jobs/new/page';

import JobHiringsPage from '../pages/job_hirings/page/page';

import { NotificationsPage } from '../pages/notifications';

import { ProfileEditPage } from '../pages/profile/edit';
import { ProfilePage } from '../pages/profile/page';

import { ContactUsPage } from '../pages/contact_us/page';

import { RestaurantsEditPage } from '../pages/restaurants/[id]/edit';
import { RestaurantsDetailPage } from '../pages/restaurants/[id]/page';
import { RestaurantsNewPage } from '../pages/restaurants/new';
import { RestaurantsPage } from '../pages/restaurants/page';

import { SignOutPage } from '../pages/sign_out';
import FastTrackPage from '../pages/fast_track/page';
import FastTrackDetailPage from '../pages/fast_track/[id]/page';

import AttendancesLayout from '../pages/attendances/page/layout';
import AttendanceDetailLayout from '../pages/attendances/[id]/layout';

import UserEditLayout from '../pages/user/edit/layout';

import PlaygroundPage from '../pages/playground';

export const rootRouter = createBrowserRouter([
  {
    path: '/pdpa_agreement',
    element: (
      <IsLoginGuard>
        <IsFindTempUserGuard>
          <ScrollRestoration />
          <RootLayout />
        </IsFindTempUserGuard>
      </IsLoginGuard>
    ),
    errorElement: <RootError />,
    children: [{ index: true, element: <PdpaAgreementPage /> }],
  },
  {
    path: '/force_change_password',
    element: (
      <IsLoginGuard>
        <IsFindTempUserGuard>
          <ScrollRestoration />
          <RootLayout />
        </IsFindTempUserGuard>
      </IsLoginGuard>
    ),
    errorElement: <RootError />,
    children: [{ index: true, element: <ForceChangePasswordPage /> }],
  },
  {
    path: '/first_profile_edit',
    element: (
      <IsLoginGuard>
        <IsFindTempUserGuard>
          <IsPdpaGuard>
            <IsForceChangePasswordGuard>
              <ScrollRestoration />
              <RootLayout />
            </IsForceChangePasswordGuard>
          </IsPdpaGuard>
        </IsFindTempUserGuard>
      </IsLoginGuard>
    ),
    errorElement: <RootError />,
    children: [{ index: true, element: <ProfileEditPage /> }],
  },
  {
    path: '/first_new_restaurant',
    element: (
      <IsLoginGuard>
        <IsFindTempUserGuard>
          <IsPdpaGuard>
            <IsInfoCompleteGuard>
              <ScrollRestoration />
              <RootLayout />
            </IsInfoCompleteGuard>
          </IsPdpaGuard>
        </IsFindTempUserGuard>
      </IsLoginGuard>
    ),
    errorElement: <RootError />,
    children: [{ index: true, element: <RestaurantsNewPage /> }],
  },

  {
    path: '/',
    // Check Login -> Check is findTemp User -> (Handle Apollo Errors) -> Check PDPA -> Check ForceChangePassword -> Check InfoComplete -> Check HaveRestaurant -> Main Layout
    element: (
      <IsLoginGuard>
        <IsFindTempUserGuard>
          <ApolloErrorThrower>
            <IsPdpaGuard>
              <IsForceChangePasswordGuard>
                <IsInfoCompleteGuard>
                  <IsHaveRestaurantGuard>
                    <ScrollRestoration />
                    <RootLayout />
                  </IsHaveRestaurantGuard>
                </IsInfoCompleteGuard>
              </IsForceChangePasswordGuard>
            </IsPdpaGuard>
          </ApolloErrorThrower>
        </IsFindTempUserGuard>
      </IsLoginGuard>
    ),
    errorElement: <RootError />,
    children: [
      {
        path: 'attendances',
        element: <AttendancesLayout />,
        children: [{ path: ':id', element: <AttendanceDetailLayout /> }],
      },
      {
        path: 'chain',
        children: [
          { index: true, element: <ProfilePage /> },
          { path: 'edit', element: <ProfileEditPage /> },
        ],
      },
      { path: 'change_phone_number', element: <Navigate to="/user/edit" replace /> },
      {
        path: 'credit',
        children: [
          { index: true, element: <CreditPage /> },
          { path: 'purchase', element: <CreditPurchasePage /> },
          { path: 'check_out_qr', element: <CheckOutQrPage /> },
          { path: 'check_out_credit_card', element: <CheckOutCreditCardPage /> },
          { path: 'check_out_credit_card_pending', element: <CheckOutCreditCardPendingPage /> },
        ],
      },
      {
        path: '/topup_result',
        element: (
          <Navigate
            to={'/credit/check_out_credit_card_pending'}
            replace
            state={{ referenceId: new URLSearchParams(window.location.search).get('referenceNo') }}
          />
        ),
      },
      {
        path: 'jobs',
        children: [
          {
            path: '',
            element: <JobsLayout />,
            children: [
              { path: ':id', element: <JobDetailLayout /> },
              { path: 'period_jobs/:id', element: <PeriodJobDetailLayout /> },
            ],
          },
          {
            path: 'new',
            element: <JobCreateLayout />,
            children: [{ index: true, element: <JobCreatePage /> }],
          },
          {
            path: 'period_jobs/new',
            element: <JobCreateLayout />,
            children: [{ index: true, element: <JobCreatePeriodPage /> }],
          },
        ],
      },
      { path: 'job_hirings', element: <JobHiringsPage /> },
      { path: 'reset_password', element: <ResetPasswordPage /> },
      {
        path: 'restaurants',
        children: [
          { index: true, element: <RestaurantsPage /> },
          {
            path: ':id',
            children: [
              { index: true, element: <RestaurantsDetailPage /> },
              { path: 'edit', element: <RestaurantsEditPage /> },
            ],
          },
          { path: 'new', element: <RestaurantsNewPage /> },
        ],
      },
      { path: 'notifications', element: <NotificationsPage /> },
      {
        path: 'profile',
        children: [{ path: 'change_password', element: <Navigate to="/user/edit" replace /> }],
      },
      {
        path: 'user',
        children: [{ path: 'edit', element: <UserEditLayout /> }],
      },
      { path: 'contact_us', element: <ContactUsPage /> },

      {
        path: 'playground',
        element:
          import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging' ? (
            <PlaygroundPage />
          ) : (
            <Navigate to="/" replace />
          ),
      },

      { path: '/', element: <Navigate to="jobs" replace /> },
    ],
  },

  // Sign Out route for forced sign out (no back button)
  {
    path: '/flow_sign_out',
    element: (
      <IsLoginGuard>
        <ScrollRestoration />
        <RootLayout />
      </IsLoginGuard>
    ),
    errorElement: <RootError />,
    children: [{ index: true, element: <SignOutPage /> }],
  },
  // Sign out route for normal sign out
  {
    path: '/sign_out',
    element: (
      <IsLoginGuard>
        <ScrollRestoration />
        <RootLayout />
      </IsLoginGuard>
    ),
    errorElement: <RootError />,
    children: [{ index: true, element: <SignOutPage /> }],
  },

  {
    path: '/fast_track',
    children: [
      {
        path: ':id',
        element: <FastTrackDetailPage />,
      },
      {
        index: true,
        element: <FastTrackPage />,
      },
    ],
  },

  {
    path: '/auth',
    element: (
      <LoggedInRedirectGuard>
        <ScrollRestoration />
        <AuthLayout />
      </LoggedInRedirectGuard>
    ),
    children: [
      { path: 'sign_in', element: <SignInPage /> },
      { path: 'reset_password', element: <ResetPasswordPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'thank_you', element: <ThankYouPage /> },
    ],
  },
]);

const RootRouter = () => {
  const { showBoundary } = useErrorBoundary();
  const { contextIsLogin, setContextIsLogin } = React.useContext(AppContext);

  const [isLoginLoading, setIsLoginLoading] = React.useState(true);
  React.useEffect(() => {
    const checkToken = async () => {
      setIsLoginLoading(true);
      try {
        const token = await tokenManager.getToken();
        setContextIsLogin(!isEmpty(token));
      } catch (error) {
        const isInvalidToken =
          get(error, 'message') === 'access_token.invalid' ||
          get(error, 'response.status') === 401 ||
          get(error, 'name') === 'InvalidTokenError';

        if (isInvalidToken) {
          setContextIsLogin(false);
        } else {
          // Other error (Network etc.) Send to ErrorBoundary
          showBoundary(error);
        }
      }
      // Wait info hook to start fetching and setLoading
      setTimeout(() => setIsLoginLoading(false), 50);
    };

    checkToken();
  }, [setContextIsLogin]);

  useSetAnalyticUserProperties();

  const { loading: infoCompleteLoading } = useLoadInfoCompleteStatusContext({ skip: !contextIsLogin });
  const { loading: restaurantsLoading } = useLoadRestaurantsContext({ skip: !contextIsLogin });
  const { loading: userInfoLoading } = useLoadUserInfoContext({ skip: !contextIsLogin });

  if (isLoginLoading || infoCompleteLoading || restaurantsLoading || userInfoLoading) {
    return <LoadingSpinner BoxProps={{ height: '100vh' }} />;
  }

  return <RouterProvider router={rootRouter} />;
};

export default RootRouter;
